import React, { useEffect, useState, useCallback } from "react";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import "./style.css";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { canAccessPath, getDefaultPathForUser } from "../utils/permissions";
import { addAppLog, sendAppLog, getLogMetaFromPath, isAdminRoutePath } from "../utils/logger";
import { getStoredUser, isAuthenticatedSession } from "../utils/authSession";

export function AdminLayout() {
  const location = useLocation();
  const user = getStoredUser();
  const isAuthenticated = !!isAuthenticatedSession();
  const isSuperAdmin = !!(user?.isSuperAdmin);
  const allowedPaths = user?.allowedPaths ?? null;
  const path = location.pathname;
  const pathAllowed = isAuthenticated && canAccessPath(path, isSuperAdmin, allowedPaths);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarOpen((open) => !open), []);

  useEffect(() => {
    closeSidebar();
  }, [path, closeSidebar]);

  useEffect(() => {
    if (!sidebarOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeSidebar();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [sidebarOpen, closeSidebar]);

  useEffect(() => {
    if (!isAuthenticated || !pathAllowed) return;
    if (isAdminRoutePath(path)) return;
    const { moduleName, programName } = getLogMetaFromPath(path);
    sendAppLog({
      pathname: path,
      logType: "Page Opened",
      content: `${moduleName} — page opened (${path})`
    });

    addAppLog(moduleName, programName, getStoredUser()?.email, "I", `Accessed ${moduleName}`);
  }, [path, isAuthenticated, pathAllowed, isSuperAdmin, allowedPaths]);

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (!pathAllowed) {
    const defaultPath = getDefaultPathForUser(isSuperAdmin, allowedPaths);
    return <Navigate to={defaultPath} replace />;
  }

  return (
    <div className="row p-0 m-0">
      <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 p-0 m-0">
        <Navbar sidebarOpen={sidebarOpen} onToggleSidebar={toggleSidebar} />
        <div className="d-flex justify-content-between">
          <div className="">
            <Sidebar open={sidebarOpen} onClose={closeSidebar} />
          </div>
          {sidebarOpen && (
            <button
              type="button"
              className="sidebar-backdrop"
              aria-label="Close navigation menu"
              onClick={closeSidebar}
            />
          )}
          <div className="p-0 w-100 main-content2">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
}
